// Boot. Wait for fonts + world data, open the world socket, mount the Svelte app.
// The UI (title / login / home / HUD) is Svelte + Tailwind over a Three.js cyber
// background; Pixi renders only the in-world canvas (see lib/world/WorldRenderer).

import "./app.css";
import { mount } from "svelte";
import { TexturePool } from "pixi.js";
import App from "./App.svelte";
import { WorldSocket } from "./net/socket.ts";
import { loadWorldData } from "./data.ts";
import { initNet, getSocket } from "./lib/stores/net.svelte.ts";
import { initPerf } from "./lib/perf.ts";
import { initUiSfx } from "./lib/uiSfx.ts";
import { initTelemetry } from "./lib/telemetry.ts";
import { WS_BASE } from "./lib/config.ts";

// ── Pixi v8 multi-Application teardown guard ────────────────────────────────────
// `TexturePool` is a GLOBAL singleton shared by every Pixi Application/renderer. We run
// several at once (the in-world canvas + the HUD/menu portrait busts). When one renderer
// is destroyed, Pixi's GlobalResourceRegistry resets the pool's internal `_texturePool`
// map but NOT its `_poolKeyHash`, so a *sibling* renderer's text-system destroy then calls
// `returnTexture()` with a now-stale key → `this._texturePool[key].push(...)` on `undefined`
// → throws. That uncaught error lands mid-Svelte-flush during a `{#key route}` screen swap
// and blanks the whole app (it was behind the blank-screen on Cancel/Close, the customizer
// stuck on "Loading…", and the deck editor never receiving its inventory). The texture is
// being discarded during teardown anyway, so if the pooled bucket is gone we drop the
// bookkeeping return instead of crashing, every renderer then tears down cleanly.
const _returnTexture = TexturePool.returnTexture.bind(TexturePool);
TexturePool.returnTexture = function guardedReturnTexture(texture, resetStyle) {
  try {
    _returnTexture(texture, resetStyle);
  } catch {
    /* pool bucket already cleared by a sibling renderer's teardown, discard, don't blank the app */
  }
};

/** Wait (briefly) for the web fonts so the first paint is already Rajdhani/Inter. */
async function loadFonts(): Promise<void> {
  try {
    await Promise.race([
      Promise.all([
        document.fonts.load("700 24px Rajdhani"),
        document.fonts.load("600 16px Rajdhani"),
        document.fonts.load("500 14px Inter"),
      ]),
      new Promise((r) => setTimeout(r, 1500)),
    ]);
  } catch {
    /* fonts optional, fall back to system sans */
  }
}

let bootedAssetCreator = false;

async function main(): Promise<void> {
  // Recovery for the silent stuck-loading-screen trap: a lazy chunk fetched mid-load can fail when
  // (a) the deploy moved on between page-open and the lazy fetch, chunk hash gone, or (b) some
  // edge intercepts the fetch and returns HTML in place of JS (CF challenge, ISP captive portal,
  // overzealous browser extension). The MIME-type error surfaces nowhere visible and the app
  // freezes at whatever % it was at. Vite dispatches `vite:preloadError` for exactly this; we
  // reload once (sessionStorage-guarded so a real bug can't loop) so the user gets fresh HTML
  // referencing live chunks, and CF gets a fresh chance to clear them through any challenge.
  window.addEventListener("vite:preloadError", () => {
    if (sessionStorage.getItem("df.preloadReloaded") === "1") return; // already retried this tab
    sessionStorage.setItem("df.preloadReloaded", "1");
    console.warn("[boot] lazy chunk failed, reloading once to recover");
    window.location.reload();
  });
  // Clear the guard after a successful idle period so a later failure can recover too.
  setTimeout(() => sessionStorage.removeItem("df.preloadReloaded"), 30_000);

  // ── Asset Creator (#/asset-creator) ────────────────────────────────────────
  // A standalone, public community tool: it shares the renderers + design system
  // but NOT the login/world socket. Mount it instead of the game app and stop.
  // A hashchange into/out of this route reloads (full isolation; no shared router).
  const isAssetCreator = () => location.hash.replace(/^#/, "").startsWith("/asset-creator");
  window.addEventListener("hashchange", () => {
    if (isAssetCreator() !== bootedAssetCreator) location.reload();
  });
  // Game-feel: synthesized hover/press blips on every interactive element
  // (document-level delegation; also covers the standalone Asset Creator).
  initUiSfx();

  // Auto graphics-tuning: pick a starting tier from the device + watch the live frame rate, tuning
  // down on weak machines so they don't peg their fans (only active while quality is on "Auto").
  initPerf();

  if (isAssetCreator()) {
    bootedAssetCreator = true;
    const { default: AssetCreator } = await import("./lib/screens/AssetCreator.svelte");
    await loadFonts();
    mount(AssetCreator, { target: document.getElementById("app")! });
    return;
  }

  // Wait for the web fonts so the first paint is already Rajdhani/Inter.
  await loadFonts();

  // Pre-fetch the world manifests so first-screen renders don't race the fetch.
  await loadWorldData();

  const socket = new WorldSocket(`${WS_BASE}/ws`);
  initNet(socket);
  mount(App, { target: document.getElementById("app")! });
  initTelemetry(); // after initNet: forwarded console errors ride the live socket (prod builds only)
  // Connect only after the permanent stores and routed UI handlers exist, so a fast
  // LoginOk/Online response cannot disappear between socket-open and first mount.
  socket.connect();

  // Dev debug handle.
  (globalThis as Record<string, unknown>).__df = { socket: getSocket };
}

void main();
